import { useState, useRef } from "react";
import { UploadCloud, X, FileIcon, ImageIcon, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { uploadMedia, deleteMedia, getMediaUrl } from "@/lib/api";

export default function FileUploadZone({ quoteId, media = [], onMediaChange, category = "photo", accept, label, helperText }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const fileInputRef = useRef(null);

  // Only show media matching this zone's category
  const visibleMedia = (media || []).filter(m => (m.category || "photo") === category && !m.is_deleted);
  const isPhotoZone = category === "photo";
  const isReceiptZone = category === "receipt";
  const isDocZone = category === "document";
  const dropAccept = accept || (isPhotoZone ? "image/*,.heic,.heif" : isReceiptZone ? "image/*,.pdf,.doc,.docx" : "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv");
  const dropLabel = label || (isPhotoZone ? "Drop photos here or click to browse" : isReceiptZone ? "Drop receipts here or click to browse" : "Drop documents here or click to browse");
  const dropHelper = helperText || (isPhotoZone
    ? "These photos will appear on the quote and invoice PDF."
    : isReceiptZone
      ? "These receipts are for your records only and will not appear on PDFs."
      : "Internal reference only — documents will not appear on any PDF.");

  const compressImage = (file, maxSize = 1600, quality = 0.8) => new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxSize && height <= maxSize && file.size < 500000) { resolve(file); return; }
      if (width > height) { if (width > maxSize) { height = Math.round(height * maxSize / width); width = maxSize; } }
      else { if (height > maxSize) { width = Math.round(width * maxSize / height); height = maxSize; } }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => { resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file); }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleFiles = async (files) => {
    if (!quoteId) return;
    setUploading(true);
    try {
      for (const file of files) {
        // Photo zone: only accept images; compress before upload
        if (isPhotoZone) {
          if (!file.type.startsWith("image/")) continue;
          const processed = await compressImage(file);
          await uploadMedia(quoteId, processed, "photo");
        } else {
          // Receipt or document zone: accept images + PDF/docs; tag with the zone's category
          await uploadMedia(quoteId, file, category);
        }
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      if (onMediaChange) onMediaChange();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) handleFiles(files);
  };

  const handleDelete = async (mediaId) => {
    try {
      await deleteMedia(quoteId, mediaId);
      toast.success("File removed");
      if (onMediaChange) onMediaChange();
    } catch (e) {
      toast.error("Failed to remove file");
    }
  };

  return (
    <div data-testid="file-upload-zone">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors cursor-pointer ${
          dragOver ? "border-orange-500 bg-orange-50" : "border-slate-300 hover:bg-slate-50"
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid="upload-dropzone"
      >
        {uploading ? (
          <Loader2 className="w-8 h-8 mx-auto text-orange-500 animate-spin" />
        ) : (
          <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
        )}
        <p className="text-sm text-slate-500 mt-2">
          {uploading ? "Uploading..." : dropLabel}
        </p>
        <p className="text-xs text-slate-400 mt-1">{dropHelper}</p>
        <input
          ref={fileInputRef}
          type="file"
          accept={dropAccept}
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files);
            if (files.length) handleFiles(files);
            e.target.value = "";
          }}
          data-testid={`file-input-${category}`}
        />
      </div>

      {/* Media list — only this zone's category */}
      {visibleMedia.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`media-list-${category}`}>
          {visibleMedia.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm p-3 group"
              data-testid={`media-item-${m.id}`}
            >
              {m.category === "photo" ? (
                <div
                  className="w-12 h-12 rounded-sm overflow-hidden bg-slate-200 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-orange-400 transition-all"
                  onClick={() => setEnlargedImage(m)}
                  data-testid={`media-thumb-${m.id}`}
                >
                  <img
                    src={getMediaUrl(m.storage_path)}
                    alt={m.original_filename}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-sm bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <FileIcon className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.original_filename}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-400">
                    {m.category === "photo" ? (
                      <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Job Photo</span>
                    ) : m.category === "receipt" ? (
                      <span className="flex items-center gap-1"><FileIcon className="w-3 h-3" /> Receipt</span>
                    ) : (
                      <span className="flex items-center gap-1"><FileIcon className="w-3 h-3" /> Document</span>
                    )}
                  </span>
                  <span className="text-xs text-slate-300">
                    {m.size ? `${(m.size / 1024).toFixed(1)} KB` : ""}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                onClick={() => handleDelete(m.id)}
                data-testid={`delete-media-${m.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Enlarged image dialog with navigation */}
      <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
        <DialogContent className="p-2" data-testid="enlarged-image-dialog" style={{ maxWidth: "90vw", width: "auto" }}>
          {enlargedImage && (() => {
            const photos = visibleMedia.filter(m => m.category === "photo");
            const currentIdx = photos.findIndex(p => p.id === enlargedImage.id);
            const hasPrev = currentIdx > 0;
            const hasNext = currentIdx < photos.length - 1;
            return (
              <div className="relative w-full">
                <img
                  src={getMediaUrl(enlargedImage.storage_path)}
                  alt={enlargedImage.original_filename}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
                />
                <p className="text-xs text-center mt-2" style={{ color: "var(--text-muted)" }}>
                  {enlargedImage.original_filename} ({currentIdx + 1}/{photos.length})
                </p>
                {/* Left arrow */}
                {hasPrev && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEnlargedImage(photos[currentIdx - 1]); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-opacity hover:opacity-100"
                    style={{ background: "rgba(0,0,0,0.5)", color: "#fff", opacity: 0.7 }}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                {/* Right arrow */}
                {hasNext && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEnlargedImage(photos[currentIdx + 1]); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-opacity hover:opacity-100"
                    style={{ background: "rgba(0,0,0,0.5)", color: "#fff", opacity: 0.7 }}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
